<template>
  <Context
    ref="context"
    max-height-if-outside-viewport
    class="select"
    @shown="onShow"
    @hidden="renamingWorkspaceId = null"
  >
    <div class="select__search">
      <i class="select__search-icon iconoir-search"></i>
      <input
        ref="search"
        v-model="query"
        type="text"
        class="select__search-input"
        :placeholder="$t('action.search')"
        tabindex="0"
      />
    </div>
    <ul
      ref="dropdown"
      v-auto-overflow-scroll
      class="select__items select__items--no-max-height dashboard__user-workspaces"
    >
      <li
        v-for="workspace in filteredWorkspaces"
        :key="workspace.id"
        class="select__item"
        :class="{
          'select__item--loading':
            workspace._.loading || workspace._.additionalLoading,
          active: workspace.id === selectedWorkspace.id,
        }"
      >
        <i
          v-if="workspace.id === selectedWorkspace.id"
          class="sidebar__workspace-active-icon iconoir-check"
        ></i>
        <a class="select__item-link" @click="selectWorkspace(workspace)">
          <div class="select__item-name">
            <Avatar
              class="dashboard__user-workspace-avatar"
              :initials="$filters.nameAbbreviation(workspace.name)"
            ></Avatar>
            <!-- Origem Flow: nome editável inline (lápis ao passar o mouse)
                 — o chrome ClickUp não tem o menu ⋮ do sidebar antigo. -->
            <Editable
              :ref="'rename-' + workspace.id"
              :value="workspace.name"
              class="dashboard__user-workspace-name"
              @click.stop="onWorkspaceNameClick(workspace, $event)"
              @change="renameWorkspace(workspace, $event)"
              @editing="renamingWorkspaceId = $event ? workspace.id : null"
            ></Editable>
            <span
              v-if="hasUnreadNotifications(workspace.id)"
              class="sidebar__unread-notifications-icon"
            ></span>
          </div>
        </a>
        <a
          v-if="
            $hasPermission('workspace.update', workspace, workspace.id) &&
            renamingWorkspaceId !== workspace.id
          "
          class="dashboard__user-workspace-rename"
          :title="$t('sidebar.renameWorkspace')"
          @click.stop="editWorkspaceName(workspace)"
        >
          <i class="iconoir-edit-pencil"></i>
        </a>
      </li>
      <li v-if="$hasPermission('create_workspace')" class="select__item">
        <a class="select__item-link" @click="$refs.createWorkspaceModal.show()">
          <div class="select__item-name">
            <ButtonIcon
              tag="a"
              size="small"
              type="secondary"
              icon="iconoir-plus"
              style="pointer-events: none"
            />
            <span>{{ $t('sidebar.addNewWorkspace') }}</span>
          </div>
        </a>
      </li>
    </ul>
    <div class="sidebar__user">
      <div class="sidebar__user-info">
        <span class="sidebar__user-email">
          {{ email }}
        </span>

        <component
          :is="component"
          v-for="(component, index) in highestLicenceTypeBadge"
          :key="index"
          class="sidebar__user-license"
        ></component>
      </div>

      <ul class="context__menu margin-bottom-0">
        <li v-if="isStaff" class="context__menu-item">
          <a class="context__menu-item-link" @click="admin">
            <i class="context__menu-item-icon iconoir-settings"></i>
            {{ $t('sidebar.adminTools') }}
          </a>
        </li>

        <li class="context__menu-item">
          <a
            class="context__menu-item-link"
            @click="$refs.settingsModal.show()"
          >
            <i class="context__menu-item-icon iconoir-user-circle"></i>
            {{ $t('sidebar.settings') }}
          </a>
          <SettingsModal ref="settingsModal"></SettingsModal>
        </li>

        <li class="context__menu-item">
          <a class="context__menu-item-link" @click="toggleTheme">
            <i class="context__menu-item-icon" :class="themeIcon"></i>
            {{ themeLabel }}
          </a>
        </li>

        <component
          :is="component"
          v-for="(component, index) in sidebarUserContextComponents"
          :key="'sidebarUserContextComponent' + index"
          @hide="hide()"
        ></component>

        <li
          class="context__menu-item context__menu-item--with-separator margin-bottom-0"
        >
          <a
            class="context__menu-item-link"
            :class="{ 'context__menu-item-link--loading': logoffLoading }"
            @click="logoff()"
          >
            <i class="context__menu-item-icon iconoir-log-out"></i>
            {{ $t('sidebar.logoff') }}
          </a>
        </li>
      </ul>
    </div>
    <CreateWorkspaceModal
      ref="createWorkspaceModal"
      @created="hide()"
    ></CreateWorkspaceModal>
  </Context>
</template>

<script>
import { mapGetters } from 'vuex'

import { logoutAndRedirectToLogin } from '@baserow/modules/core/utils/auth'
import { notifyIf } from '@baserow/modules/core/utils/error'
import context from '@baserow/modules/core/mixins/context'
import SettingsModal from '@baserow/modules/core/components/settings/SettingsModal'
import CreateWorkspaceModal from '@baserow/modules/core/components/workspace/CreateWorkspaceModal'
import Editable from '@baserow/modules/core/components/Editable'
import { escapeRegExp } from '@baserow/modules/core/utils/string'
import { pageFinished } from '@baserow/modules/core/utils/routing'
import { nextTick, useNuxtApp } from '#imports'

export default {
  name: 'SidebarUserContext',
  components: { SettingsModal, CreateWorkspaceModal, Editable },
  mixins: [context],
  props: {
    workspaces: {
      type: Array,
      required: true,
    },
    selectedWorkspace: {
      type: Object,
      required: true,
    },
  },
  emits: ['toggle-admin'],
  setup() {
    const nuxtApp = useNuxtApp()
    return { nuxtApp }
  },
  data() {
    return {
      logoffLoading: false,
      query: '',
      currentTheme: 'dark-theme',
      // Origem Flow: id do workspace com o nome em edição inline.
      renamingWorkspaceId: null,
    }
  },
  computed: {
    highestLicenceTypeBadge() {
      return Object.values(this.$registry.getAll('plugin'))
        .map((plugin) => plugin.getHighestLicenseTypeBadge())
        .filter((component) => component !== null)
    },
    adminTypes() {
      return this.$registry.getAll('admin')
    },
    sortedAdminTypes() {
      return Object.values(this.adminTypes)
        .slice()
        .sort((a, b) => a.getOrder() - b.getOrder())
    },
    sidebarUserContextComponents() {
      return Object.values(this.$registry.getAll('plugin'))
        .flatMap((plugin) =>
          plugin.getUserContextComponents(this.selectedWorkspace)
        )
        .filter((component) => component !== null)
    },
    filteredWorkspaces() {
      let workspaces = this.workspaces
      const regex = new RegExp('(' + escapeRegExp(this.query) + ')', 'i')
      if (this.query) {
        workspaces = workspaces.filter((workspace) => {
          return workspace.name.match(regex)
        })
      }
      return workspaces
    },
    ...mapGetters({
      isStaff: 'auth/isStaff',
      name: 'auth/getName',
      email: 'auth/getUsername',
    }),
    themeIcon() {
      return this.currentTheme === 'dark-theme' ? 'iconoir-sun-light' : 'iconoir-half-moon'
    },
    themeLabel() {
      const labels = {
        'pt': { 'dark-theme': 'Modo Claro', 'light-theme': 'Modo Escuro' },
        'en': { 'dark-theme': 'Light Mode', 'light-theme': 'Dark Mode' },
        'es': { 'dark-theme': 'Modo Claro', 'light-theme': 'Modo Oscuro' },
        'it': { 'dark-theme': 'Modalità Chiara', 'light-theme': 'Modalità Scura' }
      }
      const locale = this.$i18n.locale || 'pt'
      const key = this.currentTheme
      return labels[locale]?.[key] || labels['pt'][key]
    }
  },
  mounted() {
    if (import.meta.client && this.$theme) {
      // 'system' resolve para claro/escuro conforme o SO — o atalho do
      // menu sempre mostra a alternância efetiva.
      this.currentTheme = this.$theme.resolve(this.$theme.get())
    }
  },
  methods: {
    toggleTheme() {
      if (this.$theme) {
        this.currentTheme = this.$theme.toggle()
      }
    },
    onShow() {
      this.query = ''
      this.$nextTick(() => {
        this.$refs.search.focus()
      })
    },
    async logoff() {
      this.logoffLoading = true
      await logoutAndRedirectToLogin(
        this.$router,
        this.$store,
        false,
        false,
        true
      )
    },
    async admin() {
      this.hide()
      await this.$nextTick()

      this.$emit('toggle-admin', true)
      const activatedAdminTypes = this.sortedAdminTypes.filter(
        (adminType) => !adminType.isDeactivated()
      )
      try {
        await this.$router.push({ name: activatedAdminTypes[0].routeName })
        await pageFinished(this.nuxtApp)
        await nextTick()
      } catch {}
    },
    hasUnreadNotifications(workspaceId) {
      return this.$store.getters['notification/workspaceHasUnread'](workspaceId)
    },
    /**
     * Origem Flow: renomear workspace inline pelo dropdown (o chrome
     * ClickUp não tem o menu ⋮ do sidebar antigo do Origem Flow).
     */
    getRenameRef(workspace) {
      // No Vue 3, refs com string dentro de v-for viram arrays.
      const ref = this.$refs['rename-' + workspace.id]
      return Array.isArray(ref) ? ref[0] : ref
    },
    editWorkspaceName(workspace) {
      const editable = this.getRenameRef(workspace)
      if (editable) {
        editable.edit()
      }
    },
    onWorkspaceNameClick(workspace, event) {
      // Durante a edição, cliques no nome não podem trocar/navegar o
      // workspace; fora dela seguem o fluxo normal do item.
      if (this.renamingWorkspaceId !== workspace.id) {
        this.selectWorkspace(workspace)
      }
    },
    async renameWorkspace(workspace, { value, oldValue }) {
      const name = value.trim()
      if (name === '' || name === oldValue) {
        const editable = this.getRenameRef(workspace)
        if (editable) {
          editable.set(oldValue)
        }
        return
      }
      try {
        await this.$store.dispatch('workspace/update', {
          workspace,
          values: { name },
        })
      } catch (error) {
        const editable = this.getRenameRef(workspace)
        if (editable) {
          editable.set(oldValue)
        }
        notifyIf(error, 'workspace')
      }
    },
    async selectWorkspace(workspace) {
      if (workspace._.selected || this.renamingWorkspaceId === workspace.id) {
        return
      }
      await this.$store.dispatch('workspace/select', workspace)
      await this.$router.push({
        name: 'workspace',
        params: { workspaceId: workspace.id },
      })
      await pageFinished(this.nuxtApp)
      await nextTick()
      this.hide()
    },
  },
}
</script>
